// Sprint 20.8 (D013-EC-76) — Runner usage modal: real Anthropic data
// per runner.
//
// Pre-S20.8 the modal projected the runner-side `cap_5h_residual_pct`
// / `weekly_hours_*` heartbeat fields populated by
// `runner/health/cap-tracker.ts`. Those values measure "how many times
// the daemon spawned the `claude` binary in 5 h" — explicitly NOT
// authoritative (per the cap-tracker module header). The modal showed
// 1 % while Claude Code in VS Code reported 30 % for the same Max
// account; the data was effectively invented from the founder's POV.
//
// This rewrite replaces the projection with the five canonical buckets
// the Anthropic OAuth `/api/oauth/usage` endpoint returns, fetched on
// the runner side (where the OAuth tokens live post-D-013) and
// surfaced through the `RunnerUsageBridge` fan-out. Each runner row
// renders its own bars; on failure the bars are replaced by a
// founder-mode error sentence (token expired, runner offline, rate
// limited, etc.) — never an empty pill.
//
// Cursor parity (`cursor-ux-mirror.md` §2.4): same shadcn/radix Dialog
// shell, same Spinner / Icon helpers, no new pattern invented.

import { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Spinner } from './atoms/Spinner';
import { Icon } from './atoms/Icon';
import { Badge } from './atoms/Badge';
import { Button } from './ui/button';
import { useTranslation } from '../i18n';
import type {
  RunnerUsageEntry,
  RunnerUsageErrorCode,
  RunnerStatus,
  UsageBucket,
  UsageData,
  UsageLimitEntry,
} from '../lib/api';
import { formatRelativeTime } from '../lib/time';
import { deriveRunnerLabel } from './RunnerChip';
import { useRunnersUsageStore } from '../stores/runners-usage.store';

export interface UsageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Test/story injection: when set, seeds the store immediately.
  initialRunners?: RunnerUsageEntry[];
}

type ToastTKey = Parameters<ReturnType<typeof useTranslation>['t']>[0];

const STATUS_BADGE_COLOR: Record<RunnerStatus, 'success' | 'error' | 'warning' | 'muted'> = {
  online: 'success',
  offline: 'muted',
  draining: 'warning',
  incompatible_version: 'error',
};

const STATUS_LABEL_KEY: Record<RunnerStatus, ToastTKey> = {
  online: 'usage.statusOnline',
  offline: 'usage.statusOffline',
  draining: 'usage.statusDraining',
  incompatible_version: 'usage.statusIncompatible',
};

// Founder-mode copy keys for each per-runner failure mode. Translated
// inline by `t(...)` so a row that comes back with `usageErrorCode`
// renders a plain-language sentence instead of a stuck "—" or "0 %".
const ERROR_LABEL_KEY: Record<RunnerUsageErrorCode, ToastTKey> = {
  OAUTH_TOKEN_MISSING: 'usage.errorOauthMissing',
  OAUTH_TOKEN_INVALID: 'usage.errorOauthInvalid',
  RATE_LIMITED: 'usage.errorRateLimited',
  UPSTREAM_ERROR: 'usage.errorUpstream',
  TIMEOUT: 'usage.errorTimeout',
  RUNNER_OFFLINE: 'usage.errorRunnerOffline',
  BRIDGE_STOPPED: 'usage.errorUpstream',
  BRIDGE_UNCONFIGURED: 'usage.errorUpstream',
};

// INV-X: modal reads from the same `runners-usage.store` that the
// pill reads from. On open, triggers a fresh hydrate so the data is
// current; between opens the polling keeps the cache warm.
export function UsageModal({ open, onOpenChange, initialRunners }: UsageModalProps) {
  const { t } = useTranslation();
  const entriesByRunnerId = useRunnersUsageStore((s) => s.entriesByRunnerId);
  const loaded = useRunnersUsageStore((s) => s.loaded);
  const error = useRunnersUsageStore((s) => s.error);
  const hydrate = useRunnersUsageStore((s) => s.hydrate);
  const _setEntries = useRunnersUsageStore((s) => s._setEntries);

  useEffect(() => {
    if (!open) return;
    if (initialRunners) {
      _setEntries(initialRunners);
      return;
    }
    void hydrate({ fresh: true });
  }, [open, initialRunners, hydrate, _setEntries]);

  const runners = Object.values(entriesByRunnerId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Sprint usage-limits-v2 (D6): the DialogContent primitive has
          no max-height by default; a fleet with 6 runners × N limit
          bars each overflowed the viewport and was unscrollable
          because Radix's centered-transform layout locks the position.
          Constrain the shell to 80vh and let the body panel scroll
          — the header (title + description + close button) stays
          pinned. `max-h-[80vh]` uses a viewport unit which is a
          structural calc allowed by design-discipline (§Allowed
          exceptions) — not a raw pixel drift. */}
      <DialogContent
        className="sm:max-w-2xl flex flex-col max-h-[80vh] overflow-hidden"
        data-testid="usage-modal"
      >
        <DialogHeader>
          <DialogTitle>{t('usage.title')}</DialogTitle>
          <DialogDescription>{t('usage.description')}</DialogDescription>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto -mx-6 px-6"
          data-testid="usage-scroll"
        >

        {!loaded && !error && (
          <div
            className="flex items-center justify-center gap-2 py-8 text-muted-foreground"
            data-testid="usage-loading"
          >
            <Spinner size="md" />
            <span className="text-body">{t('usage.loading')}</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="usage-error">
            <Icon name="AlertCircle" size={24} className="text-status-error" />
            <p className="text-body text-muted-foreground">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void hydrate({ fresh: true })}
              data-testid="usage-retry"
            >
              {t('usage.retry')}
            </Button>
          </div>
        )}

        {loaded && !error && runners.length === 0 && (
          <p
            className="text-caption text-muted-foreground rounded-md border border-border/50 bg-card p-3"
            data-testid="usage-empty"
          >
            {t('usage.empty')}
          </p>
        )}

        {loaded && !error && runners.length > 0 && (
          <UsageBody runners={runners} />
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UsageBody({ runners }: { runners: RunnerUsageEntry[] }) {
  const { t } = useTranslation();
  const earliest = pickEarliestReset(runners);

  return (
    <div className="space-y-3" data-testid="usage-body">
      <ul className="space-y-2" data-testid="usage-runner-list">
        {runners.map((runner) => (
          <RunnerUsageRow key={runner.runnerId} runner={runner} />
        ))}
      </ul>

      <div
        className="border-t border-border/50 pt-3 text-caption text-muted-foreground"
        data-testid="usage-footer"
      >
        {earliest ? (
          t('usage.footerEarliestReset', {
            hostname: earliest.hostname,
            time: formatResetCountdown(earliest.resetIso),
          })
        ) : (
          t('usage.footerNoReset')
        )}
      </div>
    </div>
  );
}

function RunnerUsageRow({ runner }: { runner: RunnerUsageEntry }) {
  const { t } = useTranslation();
  // drain_mode is orthogonal to status on the backend, but the founder
  // needs ONE label — mirror RunnersTable.tsx's projection.
  const effectiveStatus: RunnerStatus =
    runner.status === 'online' && runner.drainMode ? 'draining' : runner.status;
  const statusColor = STATUS_BADGE_COLOR[effectiveStatus];
  const statusLabel = t(STATUS_LABEL_KEY[effectiveStatus]);
  const label = deriveRunnerLabel(runner.runnerId);
  const errorCode = runner.usage === null ? runner.usageErrorCode : undefined;

  return (
    <li
      className="rounded-md border border-border/50 bg-card p-3 space-y-3"
      data-testid="usage-row"
      data-runner-id={runner.runnerId}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Badge
            variant="dot"
            color={statusColor}
            className="w-2.5 h-2.5 shrink-0"
            aria-label={statusLabel}
          />
          <div className="flex-1 min-w-0">
            <div className="text-body text-foreground font-550 truncate">
              {label}
            </div>
            <div className="text-caption text-muted-foreground truncate">
              {runner.hostname}
            </div>
          </div>
          <Badge variant="count" color={statusColor} className="shrink-0 text-label uppercase">
            {statusLabel}
          </Badge>
        </div>
      </div>

      {runner.usage ? (
        <UsageBars usage={runner.usage} />
      ) : (
        <RunnerUsageError
          errorCode={errorCode ?? 'UPSTREAM_ERROR'}
          hostname={runner.hostname}
          lastSeen={runner.lastSeen}
        />
      )}

      <dl className="grid grid-cols-3 gap-x-4 gap-y-1 text-caption pt-1 border-t border-border/30">
        <CompactStat
          label={t('usage.colAccount')}
          value={runner.claudeAccountId ?? t('usage.capUnknown')}
        />
        <CompactStat
          label={t('usage.colStatus')}
          value={statusLabel}
        />
        <CompactStat
          label={t('usage.colLastSeen')}
          value={runner.lastSeen ? formatRelativeTime(runner.lastSeen) : '—'}
        />
      </dl>
    </li>
  );
}

// Sprint usage-limits-v2 (2026-07-08): renders the canonical `limits[]`
// array when present (v10 wire). Falls back to the legacy per-bucket
// projection (D2) when `limits` is absent — a v9 runner, or a v10
// runner whose Anthropic response predates the array shape.
function UsageBars({ usage }: { usage: UsageData }) {
  if (usage.limits && usage.limits.length > 0) {
    return <LimitsBars limits={usage.limits} extraUsage={usage.extra_usage} />;
  }
  return <LegacyBars usage={usage} />;
}

function LimitsBars({
  limits,
  extraUsage,
}: {
  limits: UsageLimitEntry[];
  extraUsage: UsageData['extra_usage'];
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {limits.map((entry, i) => (
        <LimitEntryBar
          key={`${entry.kind}-${entry.modelName ?? entry.group ?? i}`}
          entry={entry}
          testId={`usage-limit-${entry.kind}${entry.modelName ? `-${entry.modelName}` : ''}`}
        />
      ))}
      {extraUsage && (
        <UsageBar
          label={t('usage.extraUsage')}
          bucket={extraUsage}
          subLabelOverride={extraUsageSubLabel(extraUsage)}
          testId="usage-bar-extra-usage"
        />
      )}
    </div>
  );
}

function LegacyBars({ usage }: { usage: UsageData }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {usage.five_hour && (
        <UsageBar
          label={t('usage.session5h')}
          bucket={usage.five_hour}
          testId="usage-bar-five-hour"
        />
      )}
      {usage.seven_day && (
        <UsageBar
          label={t('usage.weekly7d')}
          bucket={usage.seven_day}
          testId="usage-bar-seven-day"
        />
      )}
      {usage.seven_day_sonnet && (
        <UsageBar
          label={t('usage.weeklySonnet')}
          bucket={usage.seven_day_sonnet}
          testId="usage-bar-seven-day-sonnet"
        />
      )}
      {usage.seven_day_opus && (
        <UsageBar
          label={t('usage.weeklyOpus')}
          bucket={usage.seven_day_opus}
          testId="usage-bar-seven-day-opus"
        />
      )}
      {usage.extra_usage && (
        <UsageBar
          label={t('usage.extraUsage')}
          bucket={usage.extra_usage}
          subLabelOverride={extraUsageSubLabel(usage.extra_usage)}
          testId="usage-bar-extra-usage"
        />
      )}
    </div>
  );
}

// Founder-facing label for a canonical `limits[]` kind. Kinds we don't
// recognise fall back to a humanised version of the raw string
// ("weekly_scoped" → "Weekly scoped") so a new limit kind from
// Anthropic still renders sensibly on the next runner deploy.
function labelForLimit(entry: UsageLimitEntry, t: ReturnType<typeof useTranslation>['t']): string {
  if (entry.kind === 'session') return t('usage.limitSession');
  if (entry.kind === 'weekly_all') return t('usage.limitWeeklyAll');
  if (entry.kind === 'weekly_scoped' && entry.modelName) {
    return t('usage.limitWeeklyScopedModel', { model: entry.modelName });
  }
  if (entry.kind === 'weekly_scoped') return t('usage.limitWeeklyScoped');
  // Fallback: turn "future_kind_2027" into "Future kind 2027".
  const humanised = entry.kind.replace(/_/g, ' ');
  return humanised.charAt(0).toUpperCase() + humanised.slice(1);
}

function LimitEntryBar({
  entry,
  testId,
}: {
  entry: UsageLimitEntry;
  testId: string;
}) {
  const { t } = useTranslation();
  const label = labelForLimit(entry, t);
  // Clamp at render (D4 — schema does NOT clamp; Anthropic can report
  // >100 % when the last request pushed the account past the ceiling).
  const displayPercent = Math.max(0, Math.min(100, entry.percent));
  // "Limit reached" state: either the entry declares itself active OR
  // the reported percent has already reached 100 %. Renders red +
  // "Limit reached — resets in Xh Ym" instead of the neutral countdown.
  const limitReached = entry.isActive === true || entry.percent >= 100;

  const barColor = limitReached
    ? 'bg-status-error'
    : displayPercent >= 90
      ? 'bg-status-error'
      : displayPercent >= 70
        ? 'bg-status-warning'
        : 'bg-status-success';

  const countdown = formatResetCountdown(entry.resets_at);
  const subLabel = limitReached
    ? t('usage.limitReached', { time: countdown })
    : t('usage.resetsIn', { time: countdown });

  return (
    <div
      className="space-y-1"
      data-testid={testId}
      data-limit-kind={entry.kind}
      data-limit-active={limitReached ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between text-caption">
        <span className={limitReached ? 'text-status-error font-550' : 'text-foreground'}>
          {label}
        </span>
        <span
          className={
            limitReached
              ? 'text-status-error tabular-nums font-550'
              : 'text-muted-foreground tabular-nums'
          }
        >
          {t('usage.percent', { value: Math.round(entry.percent) })}
        </span>
      </div>
      <div
        className="h-2 w-full rounded-full bg-muted overflow-hidden"
        role="progressbar"
        aria-valuenow={displayPercent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${displayPercent}%` }}
        />
      </div>
      <p
        className={
          limitReached
            ? 'text-label text-status-error'
            : 'text-label text-muted-foreground/80'
        }
      >
        {subLabel}
      </p>
    </div>
  );
}

function extraUsageSubLabel(
  bucket: UsageBucket & { spent_usd?: number; limit_usd?: number },
): string | null {
  if (typeof bucket.spent_usd === 'number' && typeof bucket.limit_usd === 'number') {
    return `$${bucket.spent_usd.toFixed(2)} / $${bucket.limit_usd.toFixed(2)}`;
  }
  return null;
}

function RunnerUsageError({
  errorCode,
  hostname,
  lastSeen,
}: {
  errorCode: RunnerUsageErrorCode;
  hostname: string;
  lastSeen: string | null;
}) {
  const { t } = useTranslation();
  const message =
    errorCode === 'RUNNER_OFFLINE'
      ? t('usage.errorRunnerOffline', {
          lastSeen: lastSeen ? formatRelativeTime(lastSeen) : '—',
        })
      : t(ERROR_LABEL_KEY[errorCode], { hostname });
  return (
    <p
      className="text-caption text-status-warning rounded-md bg-status-warning/10 px-3 py-2"
      data-testid="usage-row-error"
      data-error-code={errorCode}
    >
      {message}
    </p>
  );
}

// Per-bucket progress bar driven by the bucket's `utilization` field.
// Bar height steps up from `h-1.5` (6 px) to `h-2` (8 px) on Tailwind's
// standard spacing scale so a low single-digit utilisation like 17 %
// reads as a visible green slab rather than a hairline. Token
// discipline (design-discipline.md): only use scale values, never
// arbitrary `h-[Npx]`. The colour thresholds mirror the legacy modal:
// green default, amber at 70 %, red at 90 %.
function UsageBar({
  label,
  bucket,
  subLabelOverride,
  testId,
}: {
  label: string;
  bucket: UsageBucket;
  subLabelOverride?: string | null;
  testId: string;
}) {
  const { t } = useTranslation();
  const clamped = Math.max(0, Math.min(100, bucket.utilization));
  const barColor =
    clamped >= 90
      ? 'bg-status-error'
      : clamped >= 70
        ? 'bg-status-warning'
        : 'bg-status-success';

  const countdown = formatResetCountdown(bucket.resets_at);
  const subLabel = subLabelOverride ?? t('usage.resetsIn', { time: countdown });

  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center justify-between text-caption">
        <span className="text-foreground">{label}</span>
        <span className="text-muted-foreground tabular-nums">
          {t('usage.percent', { value: Math.round(clamped) })}
        </span>
      </div>
      <div
        className="h-2 w-full rounded-full bg-muted overflow-hidden"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${clamped}%` }}
        />
      </div>
      {subLabel && (
        <p className="text-label text-muted-foreground/80">{subLabel}</p>
      )}
    </div>
  );
}

function CompactStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col min-w-0">
      <dt className="text-label uppercase text-muted-foreground">{label}</dt>
      <dd className="text-caption text-foreground truncate">{value}</dd>
    </div>
  );
}

interface EarliestReset {
  hostname: string;
  resetIso: string;
}

// Picks the runner whose soonest future bucket reset comes first
// across ALL five buckets (not just five_hour as the pre-S20.8 modal
// did). Past timestamps are ignored — a stale resets_at from a runner
// whose response was cached by Anthropic isn't a "next reset". Returns
// null when no runner has any future reset.
export function pickEarliestReset(
  runners: RunnerUsageEntry[],
  nowMs: number = Date.now(),
): EarliestReset | null {
  let bestHost: string | null = null;
  let bestIso: string | null = null;
  let bestMs = Number.POSITIVE_INFINITY;
  for (const r of runners) {
    if (!r.usage) continue;
    // Prefer the canonical `limits[]` resets when available (v10);
    // otherwise fall back to the legacy per-bucket resets (D2).
    const resetIsoList: string[] = [];
    if (r.usage.limits && r.usage.limits.length > 0) {
      for (const entry of r.usage.limits) resetIsoList.push(entry.resets_at);
      if (r.usage.extra_usage) resetIsoList.push(r.usage.extra_usage.resets_at);
    } else {
      for (const bucket of [
        r.usage.five_hour,
        r.usage.seven_day,
        r.usage.seven_day_sonnet,
        r.usage.seven_day_opus,
        r.usage.extra_usage,
      ]) {
        if (bucket) resetIsoList.push(bucket.resets_at);
      }
    }
    for (const iso of resetIsoList) {
      const ms = Date.parse(iso);
      if (Number.isNaN(ms) || ms <= nowMs) continue;
      if (ms < bestMs) {
        bestMs = ms;
        bestHost = r.hostname;
        bestIso = iso;
      }
    }
  }
  if (bestHost === null || bestIso === null) return null;
  return { hostname: bestHost, resetIso: bestIso };
}

function formatResetCountdown(iso: string, nowMs: number = Date.now()): string {
  const diffMs = Math.max(0, Date.parse(iso) - nowMs);
  const totalMinutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return '<1m';
}
